/**
 * useNodeRegistry Hook
 * 
 * Fetches node types from backend registry API instead of using hardcoded types.
 * This ensures frontend is always in sync with backend's registered nodes.
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

export interface NodeTypeFromRegistry {
  type: string;
  label: string;
  category: string;
  description: string;
  requiresBrowser: boolean;
  requiresAI: boolean;
  inputSchema: any;
  outputSchema: any;
  badgeVariant?: 'info' | 'success' | 'warning' | 'danger';
  shortDescription?: string;
  defaultConfig?: any;
  frequencyMultiplier?: number;
}

interface UseNodeRegistryReturn {
  nodeTypes: NodeTypeFromRegistry[];
  categories: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getNodeInfo: (nodeType: string) => NodeTypeFromRegistry | undefined;
}

/**
 * Category to badge variant mapping
 */
const CATEGORY_BADGE_MAP: Record<string, 'info' | 'success' | 'warning' | 'danger'> = {
  'workflow_setup': 'warning',
  'search_discovery': 'info',
  'joining_following': 'info',
  'content_creation': 'success',
  'engagement': 'success',
  'account_care': 'warning',
  'ai_processing': 'success',
};

/**
 * Category to UI display name mapping
 */
const CATEGORY_DISPLAY_MAP: Record<string, string> = {
  'workflow_setup': 'data',
  'search_discovery': 'with group',
  'joining_following': 'with group',
  'content_creation': 'with post',
  'engagement': 'with post',
  'account_care': 'with profile',
  'ai_processing': 'ai_flow_action', // ← must match WorkflowBuilder.tsx sidebar filter list
};

// Force specific node types into intended UI groups even if backend registry
// category is still generic (e.g. fanpage nodes returned as "engagement").
const NODE_CATEGORY_OVERRIDE: Record<string, string> = {
  post_to_fanpage: 'with page',
  fanpage_comment_on_post: 'with page',
  fanpage_reply_to_comments: 'with page',
  fanpage_like_comments: 'with page',
  fanpage_moderate_comments: 'with page',
  fanpage_private_reply: 'with page',
};

/**
 * Default frequency multipliers by category
 */
const DEFAULT_FREQUENCY_MAP: Record<string, number> = {
  'workflow_setup': 1.0,
  'search_discovery': 2.0,
  'joining_following': 0.7,
  'content_creation': 1.5,
  'engagement': 1.8,
  'account_care': 2.3,
  'ai_processing': 1.2,
};

/**
 * Display label overrides for specific node types.
 * Takes precedence over auto-generated formatLabel().
 * Used for AI nodes whose auto-label can be awkward.
 */
const NODE_LABEL_OVERRIDE: Record<string, string> = {
  'ai_generate_asset':    'AI Generate Asset',
  'ai_remake_image':      'AI · Remake Image',
  'ai_grok_remake_image': 'AI · Remake Image',
  'ai_text_process':      'AI Text Process',
};

/**
 * Transform backend registry response to frontend NodeType format
 */
function transformRegistryNode(backendNode: any): NodeTypeFromRegistry {
  const category = backendNode.category || 'other';
  const nodeType = backendNode.node_type;
  const resolvedCategory = NODE_CATEGORY_OVERRIDE[nodeType] || CATEGORY_DISPLAY_MAP[category] || category;

  return {
    type: nodeType,
    label: NODE_LABEL_OVERRIDE[nodeType] || formatLabel(nodeType),
    category: resolvedCategory,
    description: backendNode.description || '',
    requiresBrowser: backendNode.requires_browser || false,
    requiresAI: backendNode.requires_ai || false,
    inputSchema: backendNode.input_schema || {},
    outputSchema: backendNode.output_schema || {},
    badgeVariant: CATEGORY_BADGE_MAP[category] || 'info',
    shortDescription: backendNode.description || '',
    defaultConfig: extractDefaultConfig(backendNode.input_schema),
    frequencyMultiplier: DEFAULT_FREQUENCY_MAP[category] || 1.0,
  };
}

/**
 * Format node_type to human-readable label
 * Example: "search_groups" -> "Search Groups"
 */
function formatLabel(nodeType: string): string {
  return nodeType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Extract default config from input schema
 */
function extractDefaultConfig(inputSchema: any): any {
  if (!inputSchema || !inputSchema.properties) {
    return {};
  }

  const config: any = {};
  
  for (const [key, prop] of Object.entries(inputSchema.properties as Record<string, any>)) {
    if (prop.default !== undefined) {
      config[key] = prop.default;
    } else if (prop.type === 'array') {
      config[key] = [];
    } else if (prop.type === 'object') {
      config[key] = {};
    } else if (prop.type === 'string') {
      config[key] = '';
    } else if (prop.type === 'integer' || prop.type === 'number') {
      config[key] = prop.minimum || 0;
    } else if (prop.type === 'boolean') {
      config[key] = false;
    }
  }

  return config;
}

export function useNodeRegistry(): UseNodeRegistryReturn {
  const [nodeTypes, setNodeTypes] = useState<NodeTypeFromRegistry[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRegistry = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all nodes from backend registry
      const response = await apiClient.getRegistryNodes();
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch node registry');
      }

      const nodes = response.data?.nodes || [];
      
      // Transform backend format to frontend format
      const transformedNodes = nodes.map(transformRegistryNode);
      
      // Extract unique categories
      const categorySet = new Set<string>(transformedNodes.map(node => node.category));
      const uniqueCategories = Array.from(categorySet).sort();

      setNodeTypes(transformedNodes);
      setCategories(uniqueCategories);
      
      console.log(`✅ Loaded ${transformedNodes.length} nodes from registry`);
    } catch (err: any) {
      console.error('❌ Failed to load node registry:', err);
      setError(err.message || 'Failed to load node types');
      
      // Fallback: Empty array (will show error in UI)
      setNodeTypes([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistry();
  }, []);

  const getNodeInfo = (nodeType: string) => {
    return nodeTypes.find(nt => nt.type === nodeType);
  };

  return {
    nodeTypes,
    categories,
    loading,
    error,
    refetch: fetchRegistry,
    getNodeInfo,
  };
}
